import { LogOutIcon } from 'lucide-react';
import React, { type FC } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../../../components/accordion/accordion.tsx';
import { NumberOrEmpty } from '../../../components/typography/number-or-empty.tsx';
import { Badge } from '../../../components/ui/badge.tsx';
import { Button } from '../../../components/ui/button.tsx';
import Text from '../../../components/ui/text.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { useSignOut } from '../../../lib/hooks/use-sign-out.ts';
import { cn } from '../../../lib/utils.ts';
import { Can } from '../../acl/can.tsx';
import { CustomerModeToggle } from '../../customer-mode/components/customer-mode-toggle.tsx';
import { IntercomLauncher } from '../../intercom/components/intercom-launcher.tsx';
import { usePlatoLayoutNavigation } from '../hooks/use-plato-layout-navigation';
import { ImpersonationBanner } from './impersonation-banner';
import {
    createGetItemClassname,
    createGetTopLevelItemClassname,
    createIsSelected,
    createIsSelectedOnlyTopLevel,
    ITEM_ICON_CLASSNAME,
} from './navigation-helpers';

export type NavigationListProps = {
    closeDrawer: () => void;
};

export const NavigationList: FC<NavigationListProps> = ({ closeDrawer }) => {
    const { t } = useLocale('common');
    const location = useLocation();
    const { navigation, activeSection } = usePlatoLayoutNavigation();
    const { signOutFn } = useSignOut();

    const isSelected = createIsSelected(activeSection, location.pathname);
    const isSelectedOnlyTopLevel = createIsSelectedOnlyTopLevel(activeSection, isSelected);
    const getItemClassname = createGetItemClassname(location.pathname, isSelected);
    const getTopLevelItemClassname = createGetTopLevelItemClassname(
        location.pathname,
        isSelectedOnlyTopLevel,
    );

    return (
        <>
            <nav className="w-full">
                <ul role="list" className="flex w-full flex-col items-start gap-3">
                    {navigation.map((item) => (
                        <React.Fragment key={item.id}>
                            <li key={item.id} className="w-full">
                                {!item.subItems && !!item.href && (
                                    <Link
                                        to={item.href}
                                        onClick={closeDrawer}
                                        className={getItemClassname(item)}
                                    >
                                        <item.icon className={ITEM_ICON_CLASSNAME} aria-hidden="true" />

                                        <Text color="inherit">{item.name}</Text>

                                        {!!item.count && (
                                            <div className="flex grow justify-end">
                                                <Badge
                                                    variant="destructive"
                                                    className="h-4 min-w-4 justify-center rounded-full px-1 py-0.5 text-[10px] font-normal"
                                                >
                                                    <NumberOrEmpty>{item.count}</NumberOrEmpty>
                                                </Badge>
                                            </div>
                                        )}
                                    </Link>
                                )}
                                {!!item.subItems && (
                                    <Accordion
                                        type="single"
                                        collapsible
                                        defaultValue={isSelectedOnlyTopLevel(item) ? item.id : undefined}
                                    >
                                        <AccordionItem value={item.id}>
                                            <AccordionTrigger className={getTopLevelItemClassname(item)}>
                                                <item.icon
                                                    className={ITEM_ICON_CLASSNAME}
                                                    aria-hidden="true"
                                                />
                                                <Text className="w-full text-left" color="inherit">
                                                    {item.name}
                                                </Text>
                                            </AccordionTrigger>
                                            <AccordionContent>
                                                <ul
                                                    role="list"
                                                    className="mt-2 flex w-full flex-col items-start gap-3"
                                                >
                                                    {item.subItems.map((subItem) => (
                                                        <li
                                                            key={subItem.id}
                                                            className="flex w-full flex-row gap-3"
                                                        >
                                                            <div
                                                                className="w-6 shrink-0"
                                                                aria-hidden="true"
                                                            />
                                                            <Link
                                                                onClick={closeDrawer}
                                                                to={subItem.href}
                                                                className={getItemClassname(subItem)}
                                                            >
                                                                <subItem.icon
                                                                    className={ITEM_ICON_CLASSNAME}
                                                                    aria-hidden="true"
                                                                />
                                                                <Text color="inherit">{subItem.name}</Text>
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </AccordionContent>
                                        </AccordionItem>
                                    </Accordion>
                                )}
                            </li>
                        </React.Fragment>
                    ))}
                </ul>
            </nav>
            <div className="grow"></div>
            <ul role="list" className="flex w-full flex-col items-end gap-3">
                <Can I="read" a="Stats" field="total_margin">
                    <li className="w-full">
                        <CustomerModeToggle />
                    </li>
                </Can>
                <li className="w-full">
                    <IntercomLauncher />
                </li>
                <li className="w-full empty:hidden">
                    <ImpersonationBanner expanded />
                </li>
                <li className="w-full">
                    <Button
                        className={cn(
                            'w-full justify-start text-sm! font-medium!',
                            'aria-selected:bg-blue-50 aria-selected:text-blue-700',
                            'text-neutral-950',
                        )}
                        variant="ghost"
                        onClick={signOutFn}
                    >
                        <LogOutIcon className="h-4 w-4" />
                        <span className="font-normal">{t('logout__button')}</span>
                    </Button>
                </li>
            </ul>
        </>
    );
};
